import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";

const MAX_TRIES = 12;

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [tries, setTries] = useState(0);
  const [appt, setAppt] = useState(null);

  useEffect(() => {
    if (!sessionId) return setState("error");
    let cancelled = false;
    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          try {
            const a = await api.get(`/appointments/by-session/${sessionId}`);
            setAppt(a.data);
          } catch (_) {}
          setState("paid");
          return;
        }
      } catch (e) {
        // ignore, retry
      }
      if (attempt >= MAX_TRIES) return setState("timeout");
      setTries(attempt + 1);
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <Navbar />
      <main className="pt-40 pb-32 px-6 md:px-12 lg:px-20">
        <div className="max-w-[900px] mx-auto">
          {state === "polling" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-start gap-6">
              <Loader2 size={44} strokeWidth={1.2} className="animate-spin" style={{ color: "var(--terra)" }} />
              <div className="mono-eyebrow">§ Finalising payment</div>
              <h1 className="serif text-5xl md:text-6xl leading-[0.98]" style={{ letterSpacing: "-0.02em" }}>
                Just a moment.
              </h1>
              <p className="text-lg" style={{ color: "var(--ink-2)" }}>
                Confirming your booking with Stripe · attempt {tries + 1} of {MAX_TRIES}
              </p>
            </motion.div>
          )}
          {state === "paid" && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <div className="flex items-center gap-3 mono-eyebrow">
                <CheckCircle2 size={16} style={{ color: "var(--olive)" }} /> Booking confirmed
              </div>
              <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.98]" style={{ letterSpacing: "-0.03em" }}>
                Your hour is <em style={{ color: "var(--terra)", fontStyle: "italic" }}>reserved.</em>
              </h1>
              <p className="mt-6 max-w-[600px] text-lg" style={{ color: "var(--ink-2)" }}>
                A confirmation is on its way to your inbox. Mr. Aggarwal will be expecting you.
              </p>

              {appt && (
                <div className="mt-12 border hairline p-8" style={{ background: "#fff" }} data-testid="booking-confirmed-card">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <Detail label="Topic" value={appt.topic?.replace(/^./, (c) => c.toUpperCase())} />
                    <Detail label="Date" value={appt.date} />
                    <Detail label="Time" value={appt.time_slot} />
                    <Detail label="Mode" value={appt.mode?.replace(/^./, (c) => c.toUpperCase())} />
                  </div>
                  {appt.mode === "online" && (
                    <div className="mt-8 pt-6 border-t hairline flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <div className="mono-eyebrow">Your meeting room</div>
                        <div className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>
                          Opens right here on the website. No downloads.
                        </div>
                      </div>
                      <Link to={`/meeting/${appt.id}`} className="btn-terra" data-testid="go-to-meeting-btn">
                        Enter meeting room <ArrowRight size={16} strokeWidth={1.5} />
                      </Link>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-12">
                <Link to="/" className="link-underline text-sm" data-testid="home-link">← Back home</Link>
              </div>
            </motion.div>
          )}
          {state === "timeout" && (
            <div>
              <h1 className="serif text-4xl md:text-5xl">Payment still confirming…</h1>
              <p className="mt-4" style={{ color: "var(--ink-2)" }}>
                This can occasionally take a minute. Please refresh in a moment, or check your email for the confirmation.
              </p>
            </div>
          )}
          {state === "error" && (
            <div>
              <h1 className="serif text-4xl md:text-5xl">Something went wrong.</h1>
              <Link to="/book" className="mt-6 inline-block btn-terra">Try booking again</Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="mono-eyebrow">{label}</div>
      <div className="serif text-xl md:text-2xl mt-2" style={{ letterSpacing: "-0.015em" }}>
        {value}
      </div>
    </div>
  );
}
